import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, Button, Divider, Checkbox, FormControlLabel } from '@mui/material';
import {
  Save as SaveIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
} from '@mui/icons-material';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import formInitialValues from '../formik/followConfig/formModels/formInitialValues';
import validationSchema from '../formik/followConfig/formModels/validationSchema';
import { followConfigFormModel } from '../formik/followConfig/formModels/followConfigFormModel';
import {
  getFollowConfigStore,
  storeCreateFollowConfig,
  storeUpdateFollowConfig,
  storeDeleteFollowConfig,
  storeRestoreFollowConfig,
} from '../../../../store/ratings/thunks';
import { alpha } from '@mui/material/styles';
import InputField from '../../../../components/formFields/InputField';
import { useResponseHandler } from '../../../../hooks/useResponseHandler';

const { formField } = followConfigFormModel;

export const FollowConfig: React.FC = () => {
  const dispatch = useDispatch();
  const { handleResponse, ResponseComponents } = useResponseHandler();

  const { follow_config_list, score_threshold } = useSelector((state: any) => state.ratingsDevices);

  const [initialVals, setInitialVals] = useState(formInitialValues);
  const [configId, setConfigId] = useState<string | null>(null);
  const [status, setStatus] = useState<number | null>(null);
  const [executeRule, setExecuteRule] = useState<boolean>(false);

  useEffect(() => {
    const currentConfig = score_threshold;

    if (currentConfig) {
      setInitialVals({
        [formField.scoreThreshold.name]: currentConfig.score || '',
      });
      setConfigId(currentConfig.id);
      setStatus(currentConfig.status);
    } else {
      setInitialVals(formInitialValues);
      setConfigId(null);
      setStatus(null);
    }
  }, [score_threshold]);

  const handleDelete = async () => {
    if (!configId) return;
    const response = await dispatch(storeDeleteFollowConfig(configId));
    await handleResponse(response, 'eliminar configuración de seguimiento', true);
  };

  const handleRestore = async () => {
    if (!configId) return;
    const response = await dispatch(storeRestoreFollowConfig(configId));
    await handleResponse(response, 'restaurar configuración de seguimiento', true);
  };

  const onSubmit = async (values: any, actions: any) => {
    const payload = {
      score: Number(values[formField.scoreThreshold.name]),
      executeRule,
    };

    let response;
    if (configId) {
      response = await dispatch(storeUpdateFollowConfig(configId, payload));
      await handleResponse(response, 'actualizar configuración de seguimiento', true);
    } else {
      response = await dispatch(storeCreateFollowConfig(payload));
      await handleResponse(response, 'crear configuración de seguimiento', true);
    }

    if (response?.ok) {
      // El backend ya se refresca desde el thunk de reglas y alertas
    }
  };

  const isDeleted = status === 0;

  return (
    <Card
      sx={{
        p: 0,
        borderRadius: 2,
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        border: '1px solid #f0f0f0',
        mb: 4,
      }}
    >
      {ResponseComponents}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid #f0f0f0',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box sx={{ p: 1, bgcolor: alpha('#4A148C', 0.08), borderRadius: 1.5, display: 'flex' }}>
          <SettingsIcon sx={{ color: '#4A148C' }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#4A148C', fontSize: '1.1rem' }}>
          Configuración de Seguimiento
        </Typography>
      </Box>

      <Formik
        initialValues={initialVals}
        validationSchema={validationSchema[0]} // Validacion yup array en formModels
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({ isSubmitting }) => (
          <Form>
            <Box
              sx={{
                p: 3,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 3,
                flexDirection: { xs: 'column', md: 'row' },
              }}
            >
              <Box sx={{ flex: 1, maxWidth: 300 }}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5, display: 'block' }}
                >
                  Puntaje máximo <span style={{ color: 'red' }}>*</span>
                </Typography>
                <InputField
                  autoComplete="off"
                  name={formField.scoreThreshold.name}
                  placeholder="Ej. 3"
                  size="small"
                  type="number"
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block', mb: 2 }}
                >
                  Puntaje límite (si es menor o igual, se genera un seguimiento de la encuesta).
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={executeRule}
                      onChange={(e) => setExecuteRule(e.target.checked)}
                      color="primary"
                      disabled={isSubmitting}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Ejecutar regla al guardar
                    </Typography>
                  }
                />
              </Box>
            </Box>

            <Box
              sx={{
                p: 2.5,
                borderTop: '1px solid #f0f0f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 2,
                bgcolor: alpha('#000', 0.01),
              }}
            >
              {isDeleted ? (
                <Button
                  type="button"
                  variant="contained"
                  color="success"
                  onClick={handleRestore}
                  disabled={isSubmitting}
                  startIcon={<RestoreIcon sx={{ fontSize: '1rem' }} />}
                  sx={{ borderRadius: 1.5, px: 3, fontWeight: 700, textTransform: 'none' }}
                >
                  Restaurar Configuración
                </Button>
              ) : (
                <>
                  {configId && (
                    <Button
                      type="button"
                      variant="outlined"
                      color="error"
                      onClick={handleDelete}
                      disabled={isSubmitting}
                      startIcon={<DeleteIcon sx={{ fontSize: '1rem' }} />}
                      sx={{ borderRadius: 1.5, px: 3, fontWeight: 700, textTransform: 'none' }}
                    >
                      Eliminar Configuración
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isSubmitting}
                    startIcon={<SaveIcon sx={{ fontSize: '1rem' }} />}
                    sx={{
                      bgcolor: '#4A148C',
                      '&:hover': { bgcolor: '#380b6b' },
                      borderRadius: 1.5,
                      px: 3,
                      fontWeight: 700,
                      textTransform: 'none',
                    }}
                  >
                    {configId ? 'Actualizar Configuración' : 'Guardar Configuración'}
                  </Button>
                </>
              )}
            </Box>
          </Form>
        )}
      </Formik>
    </Card>
  );
};
